<template>
  <div class="w-full p-4 flex flex-col">
    <section class="flex flex-col w-full mt-4 mb-8">
      <h2 class="text-2xl font-bold mb-4 text-center">パスワードを更新</h2>
      <div class="flex flex-col items-center mx-auto ">
        <MTextField
          type="password"
          label="現在のパスワード"
          name="current-password"
          placeholder="現在のパスワード"
          class="mb-6"
          :value="currentPassword"
          :invalid-message="currentPasswordInvalidMessage"
          :invalid="currentPasswordInvalidMessage.length > 0"
          :entered="currentPasswordInvalidMessage.length > 0"
          @input="onInputPassword"
          @enter="validateInputPassword"
        />
        <MTextField
          v-model="newPassword"
          type="password"
          label="新しいパスワード"
          name="new-password"
          placeholder="新しいパスワード"
          class="mb-6"
          :value="newPassword"
          :invalid-message="newPasswordInvalidMessage"
          :invalid="newPasswordInvalidMessage.length > 0"
          :entered="newPasswordInvalidMessage.length > 0"
          @input="onInputNewPassword"
          @enter="validateInputNewPassword"
        />
        <MTextField
          v-model="newPasswordToConfirm"
          type="password"
          label="新しいパスワードの再入力"
          name="new-password-to-confirm"
          placeholder="新しいパスワード"
          class="mb-6"
          :value="newPasswordToConfirm"
          :invalid-message="newPasswordToConfirmInvalidMassage"
          :invalid="newPasswordToConfirmInvalidMassage.length > 0"
          :entered="newPasswordToConfirmInvalidMassage.length > 0"
          @input="onInputNewPasswordToConfirm"
          @enter="validateInputPasswordToConfirm"
        />
        <MButton class="mt-6 w-40" :invalid="!validForm" @click="commit">
          更新する
        </MButton>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import firebase from "firebase/app";
import { Options, Vue } from "vue-class-component";
import store from "@/store";
import { awaitStudentLoaded } from "@/store/index";
import { Student } from "@/entities/student";
import MSInfoItem from "@/components/setting/MSInfoItem.vue";
import MTextField from "@/components/form/MTextField.vue";
import MButton from "@/components/form/MButton.vue";
import { signOut } from "@/api/auth";
import { Domains } from "@/env";
import { isValidStudentPassword } from "@/entities/tutor_password_policy";

@Options({
  components: {
    MSInfoItem,
    MTextField,
    MButton
  }
})
export default class ResetPassword extends Vue {
  student: Student | null = null;
  currentPassword = "";
  newPassword = "";
  newPasswordToConfirm = "";
  currentPasswordInvalidMessage = "";
  newPasswordInvalidMessage = "";
  newPasswordToConfirmInvalidMassage = "";
  useSchoolAiExclusively = false;
  from = "";

  get validForm(): boolean {
    if (
      !this.currentPassword ||
      !this.newPassword ||
      !this.newPasswordToConfirm
    )
      return false;

    if (this.newPassword !== this.newPasswordToConfirm) return false;

    // 今のパスワードは以前の形式のまま。新しいパスワードには先生と同じ条件をかける
    return (
      this.validatePassword(this.currentPassword) &&
      isValidStudentPassword(this.newPassword)
    );
  }

  get redirectTo(): string {
    return (
      new Domains({ usePrefix: true }).domains.schoolAi + "/login_for_student"
    );
  }

  onInputPassword(val: string) {
    this.currentPassword = val;
  }

  onInputNewPassword(val: string) {
    this.newPassword = val;
  }

  onInputNewPasswordToConfirm(val: string) {
    this.newPasswordToConfirm = val;
  }

  validatePassword(pw: string) {
    return /^[a-zA-Z0-9!-/:-@¥[-`{-~]{8,64}$/.test(pw);
  }

  validateInputPassword() {
    if (!this.validatePassword(this.currentPassword)) {
      this.currentPasswordInvalidMessage =
        "パスワードは半角英数字記号8文字以上で入力してください";
    } else {
      this.currentPasswordInvalidMessage = "";
    }
  }

  validateInputNewPassword() {
    if (!isValidStudentPassword(this.newPassword)) {
      this.newPasswordInvalidMessage =
        "新しいパスワードは8〜64文字で、英字と、数字または記号を含めてください（スペースと全角文字は使えません）";
    } else {
      this.newPasswordInvalidMessage = "";
    }
  }

  validateInputPasswordToConfirm() {
    if (!isValidStudentPassword(this.newPasswordToConfirm)) {
      this.newPasswordToConfirmInvalidMassage =
        "新しいパスワードは8〜64文字で、英字と、数字または記号を含めてください（スペースと全角文字は使えません）";
      return;
    } else if (
      this.newPassword.length > 0 &&
      this.newPassword !== this.newPasswordToConfirm
    ) {
      this.newPasswordToConfirmInvalidMassage =
        "確認用パスワードが一致しません。";
    } else {
      this.newPasswordToConfirmInvalidMassage = "";
    }
  }

  async commit() {
    if (!this.validForm) {
      return;
    }
    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "パスワード更新中...");
      const data = {
        currentPassword: this.currentPassword,
        newPassword: this.newPassword
      };
      const call = firebase
        .app()
        .functions("asia-northeast1")
        .httpsCallable("student_reset_password");
      const res = await call(data);
      if (res.data.status !== "success") {
        alert("予期せぬエラーが発生しました。");
        return;
      }
      alert("パスワードを更新しました。");
      store.commit("SET_LOAD_TEXT", "リダイレクトしています...");
      await signOut();
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      store.commit("SIGN_OUT");

      if (this.useSchoolAiExclusively) {
        window.location.href = this.redirectTo;
      } else {
        if (this.from === "school_ai") {
          window.location.href = this.redirectTo;
        } else {
          this.$router.replace("/login");
        }
      }
    } catch (e) {
      alert("パスワードの更新に失敗しました。入力内容を再度確認してください。");
      console.error(e);
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  async created() {
    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "");
      await awaitStudentLoaded(store);
      this.student = store.state.student;
      this.useSchoolAiExclusively =
        store.state.schoolConfig?.data.useSchoolAiExclusively ?? false;
      const allowStudentsToChangePassword =
        store.state.schoolConfig?.data.allowStudentsToChangePassword ?? false;
      if (!allowStudentsToChangePassword) {
        this.$router.push("/");
      }
      this.from = this.$route.params.from
        ? (this.$route.params.from as string)
        : "";
    } catch (e) {
      alert("エラーが発生しました。時間をおいて再度試してみてください。");
      console.error(e);
      this.$router.push("/");
    } finally {
      store.commit("SET_LOAD_TEXT", "");
      store.commit("SET_LOADING", false);
    }
  }
}
</script>
